/**
 * DecisionMethodLibraryPanel — J2-B
 *
 * Read-only card list of the 7 built-in decision methods, plus any custom
 * methods returned by the endpoint (item_type='method_library' knowledge items).
 *
 * Consumes GET /api/v1/decisions/methods via useRorosDecisionMethods.
 *
 * Each card: name, description, profile (group size, speed, buy-in level,
 * effort, recommended use cases). Custom methods rendered after built-ins with
 * a "Custom" badge.
 *
 * Used in:
 *  - DecisionRightsPage (J2-B) — embedded below the matrix editor
 *  - DecisionFormDialog (J2-C) — as a method picker
 *
 * PRD §9.7.3 + §12.3.5 task 3.
 */

import React from 'react';
import { Zap, Clock, Users, Wrench, ThumbsUp, ChevronRight } from 'lucide-react';
import { t } from '@/lib/i18n';
import { Badge } from '@/components/ui/Badge';
import { QueryPage } from '@/components/QueryPage';
import { useRorosDecisionMethods } from '@/hooks/roros/decisions';
import type { DecisionMethodProfile } from '@/types/roros/decisions';

// ─── Built-in method slugs (same values as server enum RorosDecisionMethod) ─

const BUILTIN_SLUGS = new Set([
  'autocratic',
  'consultative',
  'consent_based',
  'consensus',
  'delegation',
  'voting',
  'deliberative_alignment',
]);

// ─── Profile field display ──────────────────────────────────────────────────

interface ProfileFieldProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function ProfileField({ icon, label, value }: ProfileFieldProps) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground flex-shrink-0" aria-hidden>{icon}</span>
      <span className="text-xs text-muted-foreground">{label}:</span>
      <span className="text-xs text-foreground font-medium">{value}</span>
    </div>
  );
}

// ─── Method card ───────────────────────────────────────────────────────────

interface MethodCardProps {
  method: DecisionMethodProfile;
  isCustom: boolean;
  /** When provided, card is selectable and this is called on click */
  onSelect?: (method: DecisionMethodProfile) => void;
  isSelected?: boolean;
}

export function MethodCard({ method, isCustom, onSelect, isSelected }: MethodCardProps) {
  const isSelectable = !!onSelect;

  const handleKey = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && onSelect) {
      e.preventDefault();
      onSelect(method);
    }
  };

  return (
    <div
      data-testid={`method-card-${method.slug}`}
      role={isSelectable ? 'radio' : 'article'}
      aria-checked={isSelectable ? isSelected : undefined}
      aria-label={method.name}
      tabIndex={isSelectable ? 0 : undefined}
      onClick={isSelectable ? () => onSelect(method) : undefined}
      onKeyDown={isSelectable ? handleKey : undefined}
      className={[
        'rounded-lg border p-4 space-y-2 transition-colors',
        isSelectable
          ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500'
          : '',
        isSelected
          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
          : isSelectable
            ? 'border-border hover:border-primary-300 hover:bg-muted/30'
            : 'border-border bg-card',
      ].join(' ')}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">{method.name}</h3>
          {isCustom && (
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {t('Custom')}
            </Badge>
          )}
        </div>
        {isSelectable && isSelected && (
          <ChevronRight className="h-4 w-4 text-primary-600 flex-shrink-0" aria-hidden />
        )}
      </div>

      {/* Description */}
      <p className="text-xs text-muted-foreground leading-relaxed">{method.description}</p>

      {/* Profile */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1">
        <ProfileField icon={<Users className="h-3 w-3" />} label={t('Group')} value={method.groupSize} />
        <ProfileField icon={<Zap className="h-3 w-3" />} label={t('Speed')} value={method.speed} />
        <ProfileField icon={<ThumbsUp className="h-3 w-3" />} label={t('Buy-in')} value={method.buyInLevel} />
        <ProfileField icon={<Wrench className="h-3 w-3" />} label={t('Effort')} value={method.effort} />
      </div>

      {/* Use cases */}
      {method.recommendedUseCases.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {method.recommendedUseCases.map((uc) => (
            <span
              key={uc}
              className="inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            >
              {uc}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

interface DecisionMethodLibraryPanelProps {
  /**
   * When provided, panel becomes a picker. The onSelect callback fires with
   * the clicked method. The selectedSlug highlights the active choice.
   */
  onSelect?: (method: DecisionMethodProfile) => void;
  selectedSlug?: string;
}

export function DecisionMethodLibraryPanel({ onSelect, selectedSlug }: DecisionMethodLibraryPanelProps) {
  const query = useRorosDecisionMethods();

  return (
    <QueryPage
      query={query}
      emptyMessage={t('No decision methods available.')}
      emptyIcon={<Clock className="h-8 w-8" />}
    >
      {(methods: DecisionMethodProfile[]) => {
        const builtin = methods.filter((m) => BUILTIN_SLUGS.has(m.slug));
        const custom = methods.filter((m) => !BUILTIN_SLUGS.has(m.slug));

        return (
          <div
            /**
             * W2-I a11y fix: 'list' requires direct 'listitem' children.
             * The grid wrappers between the list container and MethodCards
             * break that requirement. In non-selectable (read-only) mode use
             * 'region' instead so aria-label is still valid and no required-
             * children rule fires. 'radiogroup' is correct for the selectable
             * mode since direct children will have role='radio'.
             */
            role={onSelect ? 'radiogroup' : 'region'}
            aria-label={t('Decision Method Library')}
            className="space-y-4"
          >
            {/* Built-in methods */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {builtin.map((method) => (
                <MethodCard
                  key={method.slug}
                  method={method}
                  isCustom={false}
                  onSelect={onSelect}
                  isSelected={selectedSlug === method.slug}
                />
              ))}
            </div>

            {/* Custom methods */}
            {custom.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {t('Custom Methods')}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {custom.map((method) => (
                    <MethodCard
                      key={method.slug}
                      method={method}
                      isCustom={true}
                      onSelect={onSelect}
                      isSelected={selectedSlug === method.slug}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }}
    </QueryPage>
  );
}
